import Link from 'next/link';
import styled from 'styled-components';
import { captureException } from '@sentry/nextjs';
import { DefaultLayout } from 'src/components/layouts';
import WithErrorHandling from 'src/components/hoc/withErrorHandling';
import { PageContext } from 'src/constants/types';
import i18n from 'src/utils/translate';
import { text, titleLarge } from 'styles-js/mixins/typography';
import GlobalSearch from 'src/modules/search/shared/components/searchBar';
import Spacer, { Size } from 'src/components/spacer';
import { setAlert } from 'src/components/alert/actions';
import { getPublic } from 'src/utils/api';
type Community = {
  groupId: number;
  name: string;
  slug: string;
};
type Props = {
  communities?: Community[];
};
type groupedCommunities = {
  [key: string]: {
    letter: string;
    communities: Community[];
  };
};
export const Page = ({
  communities
}: Props) => {
  if (!communities) return null;
  const groupedCommunitiesObj = communities.reduce((groups: groupedCommunities, community) => {
    const letter = getLetter(community.name);
    if (!groups[letter]) {
      groups[letter] = {
        letter,
        communities: [community]
      };
    } else {
      groups[letter].communities.push(community);
    }
    return groups;
  }, {});
  const groupedCommunitiesArr = Object.values(groupedCommunitiesObj);
  return <DefaultLayout seo={{
    title: 'Communities | HealthUnlocked',
    description: 'Questions, support and advice from hundreds of health communities. Join the movement!'
  }} data-sentry-element="DefaultLayout" data-sentry-component="Page" data-sentry-source-file="communities.tsx">
      <Container data-sentry-element="Container" data-sentry-source-file="communities.tsx">
        <h1>{i18n.t('Communities')}</h1>
        <GlobalSearch dataTestId="search-page-bar" placeholder={i18n.t('Enter a condition or a topic')} searchType="communities" data-sentry-element="GlobalSearch" data-sentry-source-file="communities.tsx" />
        <Spacer size={Size.L} data-sentry-element="Spacer" data-sentry-source-file="communities.tsx" />
        <h2>{i18n.t('All public communities (A-Z)')}</h2>
        <ol>
          {groupedCommunitiesArr.map(({
          letter,
          communities
        }) => <li key={letter}>
              <h3>{letter}</h3>
              <ol>
                {communities.map(({
              groupId,
              name,
              slug
            }) => <li key={groupId}>
                    <Link href={`/${slug}`}>
                      {name}
                    </Link>
                  </li>)}
              </ol>
            </li>)}
        </ol>
      </Container>
    </DefaultLayout>;
};
const getLetter = (name: string) => name.charAt(0).toUpperCase();
Page.getInitialProps = async ({
  store: {
    dispatch
  }
}: PageContext) => {
  try {
    const response = await getPublic('communities/directory');
    if (response?.status !== 200) {
      dispatch(setAlert());
      return {};
    } else {
      return {
        communities: response?.data
      };
    }
  } catch (ex) {
    captureException(ex);
    dispatch(setAlert());
  }
  return {};
};
export default WithErrorHandling(Page);
const Container = styled.div`
  @media (${({
  theme
}) => theme.underScreenSmall}) {
    padding: 15px;
  }

  h1, h2, h3 {
    ${titleLarge}
  }

  ol, li {
    margin: 0;
    padding: 0;
  }
  ol {
    list-style-type: none;
    > li {
      > ol {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
        gap: 5px 20px;
        margin-bottom: 20px;
        @media (${({
  theme
}) => theme.underScreenSmall}) {
          display: block;
        }
        > li {
          ${text}
          @media (${({
  theme
}) => theme.underScreenSmall}) {
            margin-bottom: 5px;
          }
          @media (${({
  theme
}) => theme.overScreenSmall}) {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }
  }
`;